
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import * as styles from './KpointPlayer.module.css';
import './playerLoader.css';

const mergeWidgetConfig = (config1 = {}, config2 = {}) => {
  let mergedConfig = { ...config1 };
  for (let widgetName in config2) {
    let widgetConfig = mergedConfig[widgetName];
    if (!widgetConfig) {
      mergedConfig[widgetName] = config2[widgetName];
    } else if (
      typeof widgetConfig === 'object' &&
      Array.isArray(widgetConfig.list)
    ) {
      widgetConfig.list = [...widgetConfig.list, ...config2[widgetName].list];
    } else {
      mergedConfig[widgetName] = { ...widgetConfig, ...config2[widgetName] };
    }
  }
  return mergedConfig;
};

const KpointPlayer = forwardRef((props, ref) => {
  const {
    videoId,
    fakeVideoId,
    horizontalVideoId,
    verticalVideoId,
    fakeHorizontalVideoId,
    fakeVerticalVideoId,
    host,
    params = {},
    externalWidgetsConfig = {},
    packageId,
    personalizationInfo,
    thumbnailPath,
    isVerticalVideo,
    containerClassNames = '',
    videoClassNames = '',
    state = 'PUBLISHED',
    children,
    onVideoLoad = () => {},
    onVideoRemove = () => {},
  } = props;

  const startVideoWithId = videoId => {
    // Add logic to load and play video with the given ID
    console.log('Starting video with ID:', videoId);
  };

  useImperativeHandle(ref, () => ({
    startVideoWithId,
  }));

  const [isLoading, setIsLoading] = useState(true);
  const childRef = useRef();
  const playerRef = useRef(null);

  const cleanMarkUp = () => {
    if (!childRef.current) return;
    const markupContainer = childRef.current.parentElement.querySelector(
      `.${styles?.markupTemplateContainer}`
    );
    if (markupContainer) {
      markupContainer.innerHTML = '';
    }
    childRef.current.className = `kpoint-embedded-video ${styles?.embedCode} ${styles?.notLoaded} ${videoClassNames}`;
    Array.from(childRef.current.attributes).forEach(attr => {
      if (attr.name.startsWith('data-') && !attr.name.startsWith('data-ar')) {
        childRef.current.removeAttribute(attr.name);
      }
    });
  };

  const initPlayer = async () => {
    setIsLoading(true);
    cleanMarkUp();
    try {
      const options = {
        kvideoId: isSmartPlayer()
          ? isMobile()
            ? verticalVideoId
            : horizontalVideoId
          : videoId,
        videoHost: host,
        params,
        externalWidgetsConfig,
      };

      if (packageId) {
        options.packageId = packageId;
        let packageVideoId = isSmartPlayer()
          ? isMobile()
            ? fakeVerticalVideoId ?? verticalVideoId
            : fakeHorizontalVideoId ?? horizontalVideoId
          : fakeVideoId ?? videoId;

        let packageDetails = await fetch(
          `https://${host}/api/v3/videos/${packageVideoId}/dyn/embed?id=${packageId}&state=${state}`
        );
        packageDetails = await packageDetails.json();
        const embedCode = packageDetails.videoEmbedcode;

        const parser = new DOMParser();
        const doc = parser.parseFromString(embedCode, 'text/html');
        const embedCodeDiv = doc.querySelector('div');

        if (embedCodeDiv) {
          const body = doc.querySelector('body');
          const head = doc.querySelector('head');
          const bodyElements = Array.from(body.children).filter(
            element => element !== embedCodeDiv
          );

          let scripts = [];
          bodyElements.forEach(element => {
            if (element.tagName === 'SCRIPT') {
              scripts.push(element);
            }
          });
          Array.from(head.children).forEach(element => {
            if (element.tagName === 'SCRIPT') {
              scripts.push(element);
            }
          });

          scripts.forEach(script => {
            if (script.src) {
              if (script.src.includes('videofront-vega')) {
                return;
              }
              if (document.querySelector(`script[src="${script.src}"]`)) {
                return;
              }
            } else {
              const alreadyInjected = Array.from(
                document.querySelectorAll('script:not([src])')
              ).some(existing => existing.textContent === script.innerHTML);
              if (alreadyInjected) {
                return;
              }
            }
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
            } else {
              newScript.textContent = script.innerHTML;
            }
            if (script.type === 'module') {
              newScript.type = 'module';
            }
            document.body.appendChild(newScript);
          });

          let markup = Array.from(head.children)
            .map(element => element.outerHTML)
            .join('');
          markup += bodyElements.map(element => element.outerHTML).join('');
          childRef.current.parentElement.querySelector(
            `.${styles?.markupTemplateContainer}`
          ).innerHTML = markup;

          const videoParams = JSON.parse(
            embedCodeDiv.dataset.videoParams || '{}'
          );
          options.params = { ...options.params, ...videoParams };

          if (params['add-widgets']) {
            options.params['add-widgets'] += `,${videoParams['add-widgets']}`;
          }
          options.externalWidgetsConfig = mergeWidgetConfig(
            externalWidgetsConfig,
            JSON.parse(embedCodeDiv.dataset.widgetsConfig || '{}')
          );
          options.personalizationInfo = JSON.stringify({
            ...JSON.parse(embedCodeDiv.dataset.personalizationInfo || '{}'),
            ...personalizationInfo,
          });
          options.thumbnailPath =
            embedCodeDiv.dataset.thumbnailPath || thumbnailPath;
        }
      }

      // childRef.current?.classList.remove('vertical-video');
      const playerInstance = window.kPoint?.Player(childRef.current, options);
      if (playerInstance) {
        playerRef.current = playerInstance;
        onVideoLoad(playerInstance);
        playerInstance.addEventListener('loaded', () => {
          playerRef.current.isLoaded = true;
          childRef.current.classList.remove(styles?.notLoaded);
          childRef.current.parentElement
            .querySelector(`.${styles?.playerOverlay}`)
            .classList.remove(styles?.notLoaded);
          childRef.current.parentElement
            .querySelector(`.${styles?.thumbnail}`)
            ?.classList.remove(styles?.notLoaded);
          setIsLoading(false);
        });
      } else {
        throw new Error('Player instance could not be created');
      }
      return () => {
        onVideoRemove(playerInstance);
      };
    } catch (error) {
      console.error('Failed to initialize the KpointPlayer:', error);
      setIsLoading(false);
    }
  };

  const isMobile = () =>
    typeof window !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const isSmartPlayer = () => horizontalVideoId && verticalVideoId;
  const [gccId, setGccId] = useState(
    isSmartPlayer()
      ? isMobile()
        ? verticalVideoId
        : horizontalVideoId
      : videoId
  );

  useImperativeHandle(ref, () => ({
    playVideo: () => playerRef.current?.playVideo(),
    rePlayVideo: () => {
      playerRef.current.rePlayVideo();
    },
    pauseVideo: () => {
      playerRef.current?.pauseVideo();
    },
    mute: () => {
      playerRef.current?.mute();
    },
    unmute: () => {
      playerRef.current?.unmute();
    },
    seekTo: time => {
      playerRef.current.seekTo(time);
    },
    getCurrentTime: () => {
      return playerRef.current.getCurrentTime();
    },
    getDuration: () => {
      return playerRef.current.getDuration();
    },
    getVolume: () => {
      return playerRef.current.getVolume();
    },
    setVolume: volume => {
      playerRef.current.setVolume(volume);
    },
    setWidgetsConfig: widgetsConfig => {
      playerRef.current.setWidgetsConfig(widgetsConfig);
    },
    setWidgetConfig: (widgetsConfig, widgetName) => {
      playerRef.current.setWidgetsConfig({
        [widgetName]: widgetsConfig[widgetName],
      });
    },
    loadVideoByGconfid: videoId => {
      if (!videoId) return;
      setGccId(videoId);

      if (playerRef.current) {
        playerRef.current.loadVideoByGconfid(videoId);
      }
    },
    updatePersonalizationInfo: personalizationInfo => {
      // Add additional logging for production debugging
      console.log('Updating personalization info:', personalizationInfo);
      
      if (!playerRef.current) {
        console.warn('Player reference is not available');
        return;
      }
      
      if (!playerRef.current.isLoaded) {
        console.warn('Player is not fully loaded yet');
        return;
      }
      
      try {
        playerRef.current.updatePersonalizationInfo(personalizationInfo);
        console.log('Personalization info updated successfully');
      } catch (error) {
        console.error('Error updating personalization info:', error);
      }
    },
  }));

  useEffect(() => {
    if (typeof window !== 'undefined') {
    const checkKPointAvailability = () => {
      if (window.kPoint && typeof window.kPoint.Player === 'function') {
        initPlayer();
        return true;
      }
      return false;
    };

    if (!checkKPointAvailability()) {
      const intervalId = setInterval(() => {
        if (checkKPointAvailability()) {
          clearInterval(intervalId);
        }
      }, 100);
      return () => clearInterval(intervalId);
    }
  }
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      initPlayer();
    }
  }, [videoId, packageId]);

  return (
    <>
      <div className={`${styles?.videoContainer} ${containerClassNames}`}>
        <div
          ref={childRef}
          data-ar={
            isSmartPlayer()
              ? isMobile()
                ? '9:16'
                : '16:9'
              : isVerticalVideo
              ? '9:16'
              : '16:9'
          }
          className={`kpoint-embedded-video ${styles?.embedCode} ${styles?.notLoaded} ${videoClassNames}`}
        ></div>
        <div className={`${styles?.playerOverlay} ${styles?.notLoaded}`}>
          {children}
        </div>
        {isLoading && (
          <div className="loading-container">
            <div
              className={`loading ${
                isVerticalVideo ? 'loading-vertical' : 'loading-horizontal-view'
              }`}
            >
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span
              className={`video-load ${
                isVerticalVideo ? 'load-vertical' : 'load'
              }`}
            >
              Loading...
            </span>
          </div>
        )}
        <div className={styles?.markupTemplateContainer}></div>
      </div>
    </>
  );
});

export default KpointPlayer;
